import {
  DEFAULT_MODEL_BY_PROVIDER,
  type CommandId,
  type EnvironmentApi,
  type EnvironmentId,
  type ProjectId,
  type ServerProvider,
  type ThreadEnvMode,
} from "@cmmd-forge/contracts";
import { isNonEmpty as isNonEmptyString } from "effect/String";
import { resolveDefaultSelectableProvider } from "../providerModels";
import type { Project } from "../types";

/**
 * Used only when the connection has reported no providers yet.
 *
 * Every import used to pin this pair regardless of the machine, so a project
 * added on a Codex-only connection defaulted to Claude and the failure surfaced
 * only when its first thread refused to run.
 */
const ADD_PROJECT_FALLBACK_PROVIDER = "claudeAgent";

/**
 * The default model for a project being added to `providers`' connection.
 *
 * Delegates to `resolveDefaultSelectableProvider`, which already knows which
 * snapshots are selectable and in what order. Re-deriving that here would give
 * the app two rules for the same question, and the cruder one would eventually
 * pick a provider the rest of the app rejects.
 *
 * The model is read from `DEFAULT_MODEL_BY_PROVIDER` for the SAME provider, so
 * the pair belongs together by construction — Forge has already shipped one
 * that did not, and that failure is invisible until a turn is attempted.
 */
function resolveAddProjectModelSelection(providers: readonly ServerProvider[] | undefined) {
  // The length check is gone because the resolver now reports "nothing here is
  // selectable" itself: a connection that published providers where none can
  // run used to fall through to Codex, which is the same wrong answer the empty
  // case was already guarded against.
  const provider =
    resolveDefaultSelectableProvider(providers ?? []) ?? ADD_PROJECT_FALLBACK_PROVIDER;
  return { provider, model: DEFAULT_MODEL_BY_PROVIDER[provider] };
}

function newAddProjectCommandId(): CommandId {
  return crypto.randomUUID() as CommandId;
}

function newAddProjectProjectId(): ProjectId {
  return crypto.randomUUID() as ProjectId;
}

export interface RegisterProjectInput {
  readonly api: EnvironmentApi;
  readonly environmentId: EnvironmentId;
  readonly projects: readonly Project[];
  readonly cwd: string;
  readonly defaultThreadEnvMode: ThreadEnvMode;
  readonly handleNewThread: (
    projectRef: { environmentId: EnvironmentId; projectId: ProjectId },
    options?: { envMode?: ThreadEnvMode; openBrowserPreview?: boolean },
  ) => Promise<void>;
  readonly focusMostRecentThreadForProject?: (projectRef: {
    environmentId: EnvironmentId;
    projectId: ProjectId;
  }) => void | Promise<void>;
  readonly openThread?: boolean;
  /**
   * This connection's provider snapshots, forwarded to `ensureProjectForCwd`.
   *
   * Every sidebar and command-palette import goes through here, so omitting it
   * is what made the default-model fix apply to the scratch path only — a
   * Codex-only machine still got a project pinned to Claude whose first thread
   * refused to run.
   */
  readonly providers?: readonly ServerProvider[];
}

export interface EnsureProjectForCwdInput {
  readonly api: EnvironmentApi;
  readonly environmentId: EnvironmentId;
  readonly projects: readonly Project[];
  readonly cwd: string;
  /** Defaults to the last path segment, matching how added projects are named. */
  readonly title?: string;
  /**
   * This connection's provider snapshots, used to pick the project's default
   * model. Omitted means "not reported yet", which falls back rather than
   * blocking the import.
   */
  readonly providers?: readonly ServerProvider[];
}

/**
 * Resolve the project for `cwd` on `environmentId`, creating it only when no
 * project already points there.
 *
 * The dedupe is by `cwd` rather than by id because the same directory can be
 * arrived at from more than one place — adding a workspace by hand, and (since
 * CMMD-2269) starting a thread with no workspace at all. Both need to converge
 * on one project instead of stacking duplicates.
 */
export async function ensureProjectForCwd(input: EnsureProjectForCwdInput): Promise<{
  projectId: ProjectId;
  existing: boolean;
}> {
  const cwd = input.cwd.trim();
  if (!cwd) {
    throw new Error("Project path is required.");
  }

  const existing = input.projects.find(
    (project) => project.environmentId === input.environmentId && project.cwd === cwd,
  );
  if (existing) {
    return { projectId: existing.id, existing: true };
  }

  const projectId = newAddProjectProjectId();
  const title = input.title ?? cwd.split(/[/\\]/).findLast(isNonEmptyString) ?? cwd;
  await input.api.orchestration.dispatchCommand({
    type: "project.create",
    commandId: newAddProjectCommandId(),
    projectId,
    title,
    workspaceRoot: cwd,
    defaultModelSelection: resolveAddProjectModelSelection(input.providers),
    createdAt: new Date().toISOString(),
  });
  return { projectId, existing: false };
}

export async function registerProjectAndOpenThread(input: RegisterProjectInput): Promise<{
  projectId: ProjectId;
  existing: boolean;
}> {
  const resolved = await ensureProjectForCwd({
    api: input.api,
    environmentId: input.environmentId,
    projects: input.projects,
    cwd: input.cwd,
    ...(input.providers === undefined ? {} : { providers: input.providers }),
  });

  if (resolved.existing) {
    await input.focusMostRecentThreadForProject?.({
      environmentId: input.environmentId,
      projectId: resolved.projectId,
    });
    return resolved;
  }

  if (input.openThread !== false) {
    await input.handleNewThread(
      { environmentId: input.environmentId, projectId: resolved.projectId },
      {
        envMode: input.defaultThreadEnvMode,
      },
    );
  }
  return resolved;
}

/**
 * The providers an import may honestly claim for its target connection.
 *
 * Decision 11 of the repo-first model resolves a new project's default model
 * from THAT environment's providers. Forge has exactly one provider snapshot —
 * `serverConfigAtom` is a single global atom describing the PRIMARY connection
 * — and `EnvironmentState` carries none, so there is nothing to ask a sprite.
 *
 * Rather than pin the laptop's providers onto whatever connection happens to be
 * the target, this returns them only for the primary and `undefined` otherwise,
 * which falls back. Per-environment provider snapshots are what would close the
 * gap properly; until they exist, guessing is worse than falling back, because
 * a wrong pair surfaces as a provider auth error rather than as a bad default.
 */
export function resolveImportProviders(
  targetEnvironmentId: EnvironmentId,
  primaryEnvironmentId: EnvironmentId | null,
  providers: readonly ServerProvider[],
): readonly ServerProvider[] | undefined {
  if (primaryEnvironmentId === null) return undefined;
  if (targetEnvironmentId !== primaryEnvironmentId) return undefined;
  return providers.length > 0 ? providers : undefined;
}
